import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieStore } from '../../store/cookieStore';
import styles from './CookieBanner.module.css';

export default function CookieBanner() {
  const { decided, preferences, acceptAll, rejectNonEssential, setCustom } = useCookieStore();
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics]   = useState(preferences.analytics);
  const [marketing, setMarketing]   = useState(preferences.marketing);

  if (decided) return null;

  if (showCustomize) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Cookie preferences">
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Cookie preferences</h2>
            <button className={styles.closeBtn} onClick={() => setShowCustomize(false)}>✕</button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.preferenceRow}>
              <div className={styles.prefInfo}>
                <div className={styles.prefName}>Strictly necessary</div>
                <div className={styles.prefDesc}>
                  Required for the site to function — authentication, shopping cart, security.
                  Cannot be disabled.
                </div>
              </div>
              <div className={[styles.toggle, styles.toggleAlwaysOn].join(' ')}>
                <div className={styles.toggleThumb} />
              </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.preferenceRow}>
              <div className={styles.prefInfo}>
                <div className={styles.prefName}>Analytics</div>
                <div className={styles.prefDesc}>
                  Helps us understand how visitors use Aiphra so we can improve the platform.
                  We use privacy-friendly Plausible Analytics — no personal data, no cross-site tracking.
                </div>
              </div>
              <button
                className={[styles.toggle, analytics ? styles.toggleOn : ''].join(' ')}
                onClick={() => setAnalytics(!analytics)}
                aria-pressed={analytics}
                aria-label={`${analytics ? 'Disable' : 'Enable'} analytics cookies`}
              >
                <div className={styles.toggleThumb} />
              </button>
            </div>

            <div className={styles.divider} />

            <div className={styles.preferenceRow}>
              <div className={styles.prefInfo}>
                <div className={styles.prefName}>Marketing</div>
                <div className={styles.prefDesc}>
                  Used to show you relevant ads on other platforms. We currently do not run
                  advertising campaigns — this is for future use only.
                </div>
              </div>
              <button
                className={[styles.toggle, marketing ? styles.toggleOn : ''].join(' ')}
                onClick={() => setMarketing(!marketing)}
                aria-pressed={marketing}
                aria-label={`${marketing ? 'Disable' : 'Enable'} marketing cookies`}
              >
                <div className={styles.toggleThumb} />
              </button>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button className={styles.btnGhost} onClick={rejectNonEssential}>
              Reject all optional
            </button>
            <button className={styles.btnPrimary} onClick={() => setCustom({ analytics, marketing })}>
              Save preferences
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.banner} role="dialog" aria-label="Cookie consent" aria-live="polite">
      <div className={styles.bannerInner}>
        <div className={styles.bannerText}>
          <span className={styles.bannerTitle}>We use cookies.</span>
          {' '}
          <span className={styles.bannerBody}>
            Strictly necessary cookies keep the site working. We'd also like to use
            privacy-friendly analytics (Plausible — no personal data) to improve Aiphra.
          </span>
          {' '}
          <Link to="/privacy" className={styles.bannerLink}>Privacy Policy</Link>
        </div>

        <div className={styles.bannerActions}>
          <button className={styles.btnCustomize} onClick={() => setShowCustomize(true)}>
            Manage
          </button>
          <button className={styles.btnReject} onClick={rejectNonEssential}>
            Reject optional
          </button>
          <button className={styles.btnAccept} onClick={acceptAll}>
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
